import React, { forwardRef, useCallback, useMemo } from "react";

import {
  alpha,
  Box,
  ButtonBase,
  Drawer,
  Fab,
  Grid,
  Typography,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { createFolder } from "./app/transfer";
import { useUploadEnqueue } from "./app/transferQueue";

function IconCaptionButton({
  icon,
  color,
  caption,
  onClick,
}: {
  icon: React.ReactNode;
  color: string;
  caption: string;
  onClick?: () => void;
}) {
  return (
    <ButtonBase
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        borderRadius: "12px",
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(color, 0.14),
          color,
        }}
      >
        {icon}
      </Box>
      <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
        {caption}
      </Typography>
    </ButtonBase>
  );
}

export const UploadFab = forwardRef<HTMLButtonElement, { onClick: () => void }>(
  function ({ onClick }, ref) {
    return (
      <Fab
        ref={ref}
        aria-label="上传"
        variant="circular"
        color="primary"
        size="large"
        sx={{ position: "fixed", right: 16, bottom: 16, color: "white" }}
        onClick={onClick}
      >
        <UploadIcon fontSize="large" />
      </Fab>
    );
  }
);

function UploadDrawer({
  open,
  setOpen,
  cwd,
  onUpload,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  cwd: string;
  onUpload: () => void;
}) {
  const uploadEnqueue = useUploadEnqueue();

  const handleUpload = useCallback(
    (action: string) => () => {
      const input = document.createElement("input");
      input.type = "file";
      switch (action) {
        case "photo":
          input.accept = "image/*";
          input.capture = "environment";
          break;
        case "image":
          input.accept = "image/*,video/*";
          break;
        case "file":
          input.accept = "*/*";
          break;
      }
      input.multiple = true;
      input.onchange = async () => {
        if (!input.files) return;
        const files = Array.from(input.files);
        uploadEnqueue(...files.map((file) => ({ file, basedir: cwd })));
        setOpen(false);
        onUpload();
      };
      input.click();
    },
    [cwd, onUpload, setOpen, uploadEnqueue]
  );

  const takePhoto = useMemo(() => handleUpload("photo"), [handleUpload]);
  const uploadImage = useMemo(() => handleUpload("image"), [handleUpload]);
  const uploadFile = useMemo(() => handleUpload("file"), [handleUpload]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{ sx: { borderRadius: "16px 16px 0 0" } }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: 2,
          backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.2),
          mx: "auto",
          mt: 1.5,
        }}
      />
      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1.5, px: 1 }}
        >
          上传到{cwd ? ` ${cwd.replace(/\/$/, "")}` : "首页"}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={3}>
            <IconCaptionButton
              icon={<CameraIcon />}
              color="#07c160"
              caption="拍照"
              onClick={takePhoto}
            />
          </Grid>
          <Grid item xs={3}>
            <IconCaptionButton
              icon={<ImageIcon />}
              color="#4098fc"
              caption="照片/视频"
              onClick={uploadImage}
            />
          </Grid>
          <Grid item xs={3}>
            <IconCaptionButton
              icon={<UploadFileIcon />}
              color="#fa8c16"
              caption="文件"
              onClick={uploadFile}
            />
          </Grid>
          <Grid item xs={3}>
            <IconCaptionButton
              icon={<CreateNewFolderIcon />}
              color="#f7b52c"
              caption="新建文件夹"
              onClick={async () => {
                setOpen(false);
                await createFolder(cwd);
                onUpload();
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

export default UploadDrawer;
